import { ButtonGroup, Button, DataTable, Text, Box } from '@shopify/polaris';
import { EditIcon, ViewIcon } from '@shopify/polaris-icons';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import { handleSort } from '#client/utils/helpers';

/**
 *
 * @param {*} param
 * @param {Array} param.receivingRows - Array of receiving rows to be displayed in the table
 * @param {Function} param.navigate - Function to navigate to a different route
 * @param {number} param.receivingSortColumnIndex - Current sorted column index for the receiving documents
 * @param {string} param.receivingSortDirection - Current sort direction for the receiving documents
 * @param {Function} param.setReceivingSortColumnIndex - Function to set the receiving sort column index
 * @param {Function} param.setReceivingSortDirection - Function to set the receiving sort direction
 * @param {Function} param.setReceivingRows - Function to set the receiving rows state
 * @description ReceivingDocumentsTable component displays a table of receiving documents with their details and actions.
 * @returns
 */
const ReceivingDocumentsTable = ({
    receivingRows,
    navigate,
    receivingSortColumnIndex,
    receivingSortDirection,
    setReceivingSortColumnIndex,
    setReceivingSortDirection,
    setReceivingRows,
    parentData,
}) => {
    const columnKeyMap = ['id', 'status', 'createdAt', 'updatedAt', 'totalCartonLines', 'quantityReceived'];
    const purchaseOrder = parentData?.purchaseOrder || null;

    return (
        <>
            <Text variant="headingLg">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_ASN.RECEIVING_DOCUMENTS_TABLE.RECEIVING_DOCUMENTS}</Text>
            <Box paddingBlock="200">
                <Text as="p">
                    {PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_DETAIL.TOTAL_ITEMS_PURCHASE_ORDER}: {purchaseOrder?.numberOfItemsOnPurchaseOrder}
                </Text>
                <Text as="p">
                    {PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_DETAIL.TOTAL_ITEMS_IN_COMPLETED_RECEIVING_DOCUMENTS}: {purchaseOrder?.numberOfItemsInCompletedReceivingDocuments}
                </Text>
            </Box>
            <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={[
                    `${PURCHASE_ORDER_STRINGS.DOCUMENT_ID}`,
                    `${PURCHASE_ORDER_STRINGS.STATUS}`,
                    `${PURCHASE_ORDER_STRINGS.DATE_CREATED}`,
                    `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_DETAIL.RECEIVING_DOCUMENTS_TABLE.LAST_SAVE_DATE}`,
                    `${PURCHASE_ORDER_STRINGS.QTY_RECEIVED}`,
                    `${PURCHASE_ORDER_STRINGS.ACTIONS}`,
                ]}
                rows={receivingRows.map((row, index) => {
                    const advancedShippingNotices = parentData?.advancedShippingNotices || parentData?.purchaseOrder?.advancedShippingNotices;
                    const asn = advancedShippingNotices.find((notice) => String(notice.id) === String(row.advancedShippingNoticeId));

                    // Use the split method to get everything before the - in the location method. This
                    // will prevent the need for any special logic for escaping characters or regex syntax errors
                    const locationName = parentData?.purchaseOrder?.locationName;

                    return [
                        `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_RECEIVING_HEADER.RD}${locationName ? locationName?.split('-')[0] : ''}-${row.id}`,
                        <CustomBadge key={`receiving-row-${index}`} status={row.status} />,
                        row.createdAt,
                        row.updatedAt,
                        row.quantityReceived,
                        <ButtonGroup align="end" gap="0" key={`rd-${row.id}`}>
                            <Button
                                key={`receiving-row-${index}}`}
                                onClick={() =>
                                    navigate(`${row.advancedShippingNoticeId ? `advanced-shipping-notice/${asn.id}/receiving-document/${row.id}` : `receiving-document/${row.id}`}`, {
                                        state: {
                                            poId: row.id,
                                            status: row.status,
                                            netsuiteId: parentData?.netsuiteId,
                                            asnNetsuiteId: asn?.netsuiteId,
                                            parentDataStatus: parentData?.status,
                                            isAuditRequired: parentData?.isAuditTrigger,
                                            parentData: parentData,
                                            navigateFromASN: true,
                                        },
                                    })
                                }
                                icon={ViewIcon}
                                variant="monochromePlain"
                            />
                            {row.status === 'pending' && (
                                <Button
                                    key={`receiving-row-${index}`}
                                    onClick={() =>
                                        navigate(
                                            `${row.advancedShippingNoticeId ? `advanced-shipping-notice/${row.advancedShippingNoticeId}/receiving-document/${row.id}` : `receiving-document/${row.id}`}`,
                                            {
                                                state: {
                                                    poId: row.id,
                                                    status: 'pending',
                                                    netsuiteId: parentData?.netsuiteId,
                                                    asnNetsuiteId: asn?.netsuiteId,
                                                    parentDataStatus: parentData?.status,
                                                    isAuditRequired: parentData?.isAuditTrigger,
                                                    parentData: parentData,
                                                    navigateFromASN: false,
                                                },
                                            }
                                        )
                                    }
                                    icon={EditIcon}
                                    variant="monochromePlain"
                                />
                            )}
                        </ButtonGroup>,
                    ];
                })}
                sortable={[true, true, true, true, true, false]}
                defaultSortDirection="none"
                onSort={(index, direction) => {
                    setReceivingSortColumnIndex(index);
                    setReceivingSortDirection(direction);
                    const sorted = handleSort(receivingRows, index, direction, columnKeyMap);
                    setReceivingRows(sorted);
                }}
                sortDirection={receivingSortDirection}
                sortedColumnIndex={receivingSortColumnIndex}
            />
        </>
    );
};

export default ReceivingDocumentsTable;
